import PageComponentForHandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { useAppDispatch, useAppTranslation } from "@/utils";
import {
  Alert,
  Button,
  Col,
  Empty,
  Flex,
  LegacyCard,
  Row,
  Spin,
  Typography,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { FC, ReactNode, useState } from "react";
import { bnplMerchantGetContract } from "@payping/redux/actions";
import { ActivationSteps } from "@payping/api/app/api/bnplMerchantTypes";
import { CuToman } from "@payping/icons";
import Chart from "./Chart";
import BnplManageCreditPageSetCreditModal from "./SetCreditModal";
import BnplActivationContractDrawer from "../Activation/Contract";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface CardProps {
  amount: number;
  label: string;
  postAmount?: ReactNode;
}
const CardItem: FC<CardProps> = ({ amount, label, postAmount }) => {
  return (
    <LegacyCard.Ant size="small" className="tw:h-full tw:w-full">
      <Flex vertical gap={"large"} className="tw:w-full">
        <Flex align="center" justify="space-between" className="tw:w-full">
          <Flex align="center">
            <Typography.Title
              level={5}
              className="tw:m-0!"
            >{`${formatLocalizedNumber({ value: amount, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
            <CuToman width={24} height={24} />
          </Flex>
          {postAmount}
        </Flex>
        <Flex align="center" justify="space-between" className="tw:w-full">
          <Typography.Text type="secondary">{label}</Typography.Text>
        </Flex>
      </Flex>
    </LegacyCard.Ant>
  );
};

const BnplManageCreditPage = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [creditModalState, setCreditModalState] = useState(false);
  const [openContractDrawer, setOpenContractDrawer] = useState(false);

  const handleShowCreditModal = () => {
    setCreditModalState(true);
  };

  const handleHideCreditModal = () => {
    setCreditModalState(false);
  };

  const handleOpenContractDrawer = () => {
    setOpenContractDrawer(true);
  };

  const handleCloseContractDrawer = () => {
    setOpenContractDrawer(false);
  };

  const getContractState = useMessagedAsyncRetry(() => dispatch(bnplMerchantGetContract()), [dispatch], {
    success: false,
  });

  const contract = getContractState.value;

  const totalCredit = contract?.totalCredit ?? 0;
  const availableCredit = contract?.availableCredit ?? 0;
  const usageCredit = contract?.consumedCredit ?? 0;

  return (
    <>
      <PageComponentForHandlingCrispInMobile>
        {getContractState.loading ? (
          <Flex className="tw:h-48" justify="center" align="center">
            <Spin />
          </Flex>
        ) : (
          <Flex vertical gap={"large"}>
            {contract?.next === ActivationSteps.WaitingForAdmin ? (
              <Alert title={t("shop.bnpl.activation.underActivatian")} type="info" showIcon />
            ) : null}
            {contract?.next === ActivationSteps.SignContract ? (
              <Alert
                title={t("shop.bnpl.activation.contract.readyAlert.title")}
                description={
                  <Flex gap={"middle"} className="tw:w-full" vertical>
                    <Typography.Text>{t("shop.bnpl.activation.contract.readyAlert.content")}</Typography.Text>
                    <div>
                      <Button onClick={handleOpenContractDrawer} type="primary">
                        {t("shop.bnpl.activation.contract.readyAlert.action")}
                      </Button>
                    </div>
                  </Flex>
                }
                type="info"
                showIcon
              />
            ) : null}

            <Flex vertical gap={"large"}>
              <Flex vertical gap="small">
                <Typography.Title level={4} className="tw:m-0!">
                  {t("shop.bnpl.manageCredit.title")}
                </Typography.Title>
                <Typography.Text type="secondary">{t("shop.bnpl.manageCredit.content")}</Typography.Text>
              </Flex>
              <Row gutter={[16, 16]}>
                <Col span={24} md={24} lg={12}>
                  <CardItem
                    amount={totalCredit ?? 0}
                    label={t("shop.bnpl.manageCredit.cards.setCurrentLimit.title")}
                    postAmount={
                      (totalCredit ?? 0) > 0 ? (
                        <Button type="link" onClick={handleShowCreditModal}>
                          {t("shop.bnpl.manageCredit.cards.setCurrentLimit.updateCredit")}
                        </Button>
                      ) : (
                        <Button type="primary" onClick={handleShowCreditModal}>
                          {t("shop.bnpl.manageCredit.cards.setCurrentLimit.setCredit")}
                        </Button>
                      )
                    }
                  />
                </Col>
                <Col span={24} md={12} lg={12}>
                  <CardItem amount={usageCredit ?? 0} label={t("shop.bnpl.manageCredit.cards.usageCredit.title")} />
                </Col>
                {/* <Col span={24} md={12} lg={8}>
                <CardItem amount={1000} label={t("shop.bnpl.manageCredit.cards.recurringLimit.title")} />
              </Col> */}
              </Row>
            </Flex>

            {getContractState.value?.creditCode && (totalCredit ?? 0) ? (
              <Chart
                creditCode={getContractState.value?.creditCode}
                availableCredit={availableCredit ?? 0}
                totalCredit={totalCredit ?? 100}
              />
            ) : (
              <div className="tw:mt-36!">
                <Empty
                  description={
                    <Typography.Title level={5} className="tw:m-0!">
                      {t("shop.bnpl.manageCredit.chart.empty")}
                    </Typography.Title>
                  }
                />
              </div>
            )}
          </Flex>
        )}
      </PageComponentForHandlingCrispInMobile>
      {creditModalState ? (
        <BnplManageCreditPageSetCreditModal
          limitCredit={usageCredit ?? 0}
          open={creditModalState}
          onCancel={handleHideCreditModal}
          creditCode={contract?.creditCode ?? ""}
          onFinish={() => {
            handleHideCreditModal();
            getContractState.retry();
          }}
        />
      ) : null}

      {openContractDrawer ? (
        <BnplActivationContractDrawer onClose={handleCloseContractDrawer} open={openContractDrawer} />
      ) : null}
    </>
  );
};

export default BnplManageCreditPage;
